import { useCallback, useRef, useState } from 'preact/hooks';

/** 移动端底部聊天面板高度范围(视口百分比) */
export const SHEET_MIN = 30;
export const SHEET_MAX = 85;
export const SHEET_DEFAULT = 50;

/**
 * 由起始高度 + 竖向拖拽位移算出新高度(百分比)并 clamp 到 [SHEET_MIN, SHEET_MAX]。
 * dyUp 为「向上」位移(startY - currentY),向上拖动 → 面板变高。纯函数,便于单测。
 */
export function computeSheetHeight(
  startHeightPct: number,
  dyUp: number,
  viewportH: number,
): number {
  const vh = viewportH || 1;
  const next = startHeightPct + (dyUp / vh) * 100;
  return Math.max(SHEET_MIN, Math.min(SHEET_MAX, next));
}

/**
 * 移动端底部聊天面板的拖拽 hook —— 复刻设计稿 motion `drag="y"`,改用 pointer 事件实现。
 * 把手元素绑定返回的 handleProps;用 setPointerCapture 保证手指移出把手后仍持续接收 move。
 * 返回的 heightPct 由调用方写入 CSS 变量 --dcs-sheet-h 驱动面板高度(每帧只改变量,不重渲消息列表)。
 * 桌面端把手 display:none、heightPct 不参与布局,hook 仍照常调用以保持 hooks 顺序稳定。
 */
export function useDragSheet() {
  const [heightPct, setHeightPct] = useState(SHEET_DEFAULT);
  const dragging = useRef(false);
  const startY = useRef(0);
  const startH = useRef(SHEET_DEFAULT);

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      dragging.current = true;
      startY.current = e.clientY;
      startH.current = heightPct;
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        /* jsdom / 不支持 PointerCapture 时忽略 */
      }
    },
    [heightPct],
  );

  const onPointerMove = useCallback((e: PointerEvent) => {
    if (!dragging.current) return;
    const dyUp = startY.current - e.clientY; // 上拖 → 变高
    setHeightPct(computeSheetHeight(startH.current, dyUp, window.innerHeight));
  }, []);

  const onPointerUp = useCallback((e: PointerEvent) => {
    dragging.current = false;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  return {
    heightPct,
    handleProps: { onPointerDown, onPointerMove, onPointerUp },
  };
}
